import { NavLink } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useReconnectStatus, type ReconnectAccount } from '../../api/hooks';

const PROVIDER_LABEL: Record<string, string> = {
  microsoft: 'Outlook',
  google: 'Google',
};

function providerLabel(provider: string): string {
  return PROVIDER_LABEL[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** e.g. "Lisa's Outlook" / "Ken's Google" (falls back to email when no member). */
function accountLabel(a: ReconnectAccount): string {
  const who = a.members.length > 0 ? `${a.members.join(' & ')}'s ` : '';
  return `${who}${providerLabel(a.provider)}`;
}

/**
 * A small warning shown next to the Settings button when one or more family
 * calendars have lost their connection and need the owner to reconnect. Clicking
 * it opens Settings → Integrations. Renders nothing when everything is healthy.
 */
export function ReconnectWarning({ collapsed = false }: { collapsed?: boolean }) {
  const { data } = useReconnectStatus();

  if (!data?.needsReconnect || data.accounts.length === 0) return null;

  const count = data.accounts.length;
  // Full detail in the hover tooltip (each account + the reason).
  const title = data.accounts
    .map((a) => `${accountLabel(a)} calendar — ${a.reason}`)
    .join('\n');
  const ariaLabel = `${count} calendar${count > 1 ? 's' : ''} need reconnecting — open Integrations settings`;

  if (collapsed) {
    return (
      <NavLink
        to="/settings?tab=integrations"
        title={title}
        aria-label={ariaLabel}
        data-testid="reconnect-warning"
        className="relative flex items-center justify-center py-3 rounded-xl text-coral hover:bg-coral/10 transition-all duration-200"
      >
        <AlertTriangle className="w-6 h-6" />
        <span className="absolute top-1.5 right-3 w-2.5 h-2.5 rounded-full bg-coral ring-2 ring-bg-primary" />
      </NavLink>
    );
  }

  const first = data.accounts[0];
  const label =
    count === 1 && first
      ? `${accountLabel(first)} needs reconnecting`
      : `${count} calendars need reconnecting`;

  return (
    <NavLink
      to="/settings?tab=integrations"
      title={title}
      aria-label={ariaLabel}
      data-testid="reconnect-warning"
      className="flex items-center gap-2 px-4 py-2 mx-2 mb-1 rounded-xl bg-coral/10 text-coral hover:bg-coral/15 transition-all duration-200"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span className="text-xs font-medium leading-tight">{label}</span>
    </NavLink>
  );
}
